import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, Reorder, motion, useDragControls, useReducedMotion } from 'framer-motion';
import { AlertCircle, ChevronDown, ChevronUp, ListMusic, Plus, Trash2 } from 'lucide-react';
import { useApp } from '../../../contexts/AppContext';
import { useToast } from '../../ui/Toast';
import { PLAYLIST_MAX_ITEMS } from '../../../utils/playlist';
import { tileStatusFor } from '../../../utils/playlistDelivery';
import { TONE_CLASSES } from '../../../utils/tones';
import { pageTransitions, springs } from '../../../utils/animations';
import { haptics } from '../../../utils/haptics';
import PlaylistItemTile from './PlaylistItemTile';
import TileConfirmPopover from './TileConfirmPopover';
import DeliveryStatusStrip, { DeliveryPanel, UNDO_WINDOW_MS } from './DeliveryStatusStrip';
import {
  CAP_WARNING_AT,
  COPY,
  SAVED_COPY,
  announce as copy,
  carouselHeading,
  clearConfirm,
  countLine,
  removeConfirm,
  saveFailed,
  sheetHandleLabel,
} from './playlistCopy';

export const DOCK_BOTTOM = 'calc(4rem + max(env(safe-area-inset-bottom), 0.5rem))';
const HELP_ID = 'playlist-reorder-help';
const HEADING_ID = 'playlist-carousel-heading';
const EDGE_SCROLL_ZONE = 48;
const EDGE_SCROLL_STEP = 12;
const SHEET_CLOSE_OFFSET = 120;
const SHEET_CLOSE_VELOCITY = 500;

function MiniThumbs({ items, max, flightTarget, small = false }) {
  const shown = items.slice(-max);
  return (
    <div className={`flex flex-shrink-0 ${small ? '-space-x-4' : '-space-x-3'}`} aria-hidden="true" data-flight-target={flightTarget}>
      {shown.map((item) => (
        <div
          key={item.itemKey}
          className={`${small ? 'w-8 h-[18px]' : 'w-10 h-[22px]'} rounded-md overflow-hidden ring-2 ring-white dark:ring-elevated-dark-1 bg-gray-200 dark:bg-elevated-dark-3`}
        >
          {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />}
        </div>
      ))}
    </div>
  );
}

function CountLine({ items }) {
  const text = countLine(items);
  const tone = items.length >= CAP_WARNING_AT ? TONE_CLASSES.warning : 'text-gray-900 dark:text-gray-100';
  return (
    <p className={`text-body-sm font-semibold tabular-nums overflow-hidden whitespace-nowrap ${tone}`} data-testid="playlist-count">
      <motion.span
        key={text}
        className="inline-block"
        initial={{ y: 8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {text}
      </motion.span>
    </p>
  );
}

// §5.5 (4): one soft glow sweeps across the dock on success. Not under reduced motion.
function GlowSweep({ runKey }) {
  const reduceMotion = useReducedMotion();
  if (reduceMotion || !runKey) return null;
  return (
    <motion.div
      key={runKey}
      aria-hidden="true"
      data-testid="playlist-glow-sweep"
      className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 0.18, 0] }}
      transition={{ duration: 0.9, times: [0, 0.4, 1] }}
    >
      <motion.div
        className="absolute inset-y-0 -left-1/2 w-1/2 bg-gradient-to-r from-transparent via-primary-400/40 to-transparent"
        initial={{ x: '0%' }}
        animate={{ x: '400%' }}
        transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
      />
    </motion.div>
  );
}

function CapNotice() {
  return (
    <p className={`flex items-center gap-1.5 text-caption ${TONE_CLASSES.warning}`} data-testid="playlist-cap-notice">
      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
      {COPY.capNotice}
    </p>
  );
}

function EmptyGuidance({ isRow, canvaConnected, onAdd }) {
  return (
    <div className={`flex ${isRow ? 'flex-col items-center text-center py-8 px-6 gap-4' : 'items-center gap-5 py-1'}`}>
      <button
        type="button"
        onClick={onAdd}
        data-testid="playlist-empty-add"
        className="group relative flex-shrink-0 w-[128px] xl:w-[152px] aspect-video squircle-sm border-2 border-dashed border-primary-400/70 dark:border-primary-400/50 bg-primary-50/60 dark:bg-primary-900/10 flex flex-col items-center justify-center gap-1 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-elevated-dark-1 transition-colors"
      >
        <span className="absolute top-1 left-1 w-6 h-6 rounded-full bg-primary-600 text-white text-caption font-semibold flex items-center justify-center" aria-hidden="true">1</span>
        <Plus className="w-6 h-6 transition-transform group-hover:scale-110" strokeWidth={2.5} />
        <span className="text-body-sm font-semibold">{COPY.addTile}</span>
      </button>
      <div className="min-w-0 max-w-xl">
        <p className="text-heading text-gray-900 dark:text-gray-100">{COPY.emptyTitle}</p>
        <p className="mt-1 text-body-sm text-gray-600 dark:text-gray-300">
          {canvaConnected ? COPY.emptyBody : COPY.emptyBodyDisconnected}
        </p>
      </div>
    </div>
  );
}

/**
 * The always-visible playlist: a docked horizontal strip at ≥768 px and a
 * bottom sheet (peek bar ↔ expanded list) below. Position = delivered order.
 */
export default function PlaylistCarousel({
  layout,
  open,
  onOpenChange,
  locked,
  canvaConnected,
  arrivingKey,
  onFocusSearch,
  announce,
  deliverControl,
  deliveryPanelProps,
}) {
  const {
    state,
    removeFromPlaylist,
    restorePlaylistItem,
    movePlaylistItem,
    setPlaylistOrder,
    clearPlaylist,
    newPlaylist,
    savePlaylist,
  } = useApp();
  const toast = useToast();
  const { items, delivery } = state.playlist;
  const isSheet = layout === 'sheet';
  const delivered = delivery.status === 'done';
  const reduceMotion = useReducedMotion();

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const listRef = useRef(null);
  const scrollerRef = useRef(null);
  const pendingFocusRef = useRef(null);
  const [undo, setUndo] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const sheetDrag = useDragControls();

  const focusAddTarget = useCallback(() => {
    listRef.current?.querySelector('[data-add-tile], [data-testid="playlist-empty-add"]')?.focus({ preventScroll: true });
  }, []);

  useLayoutEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending) return;
    pendingFocusRef.current = null;
    if (!pending.key) {
      focusAddTarget();
      return;
    }
    const tile = listRef.current?.querySelector(`[data-testid="playlist-item"][data-item-key="${pending.key}"]`);
    if (!tile) return;
    const button = pending.control && tile.querySelector(`[data-testid="playlist-item-move-${pending.control}"]`);
    (button && !button.disabled ? button : tile).focus({ preventScroll: true });
    tile.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [items, focusAddTarget, reduceMotion]);

  const previousCount = useRef(items.length);
  useEffect(() => {
    const grew = items.length > previousCount.current;
    previousCount.current = items.length;
    if (!grew || !open) return;
    const last = listRef.current?.querySelector(`[data-item-key="${items[items.length - 1].itemKey}"]`);
    last?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [items, open, reduceMotion]);

  useEffect(() => {
    if (!undo) return undefined;
    const id = setTimeout(() => setUndo(null), UNDO_WINDOW_MS);
    return () => clearTimeout(id);
  }, [undo]);

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || isSheet) return;
    setEdges({
      start: el.scrollLeft > 1,
      end: el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, [isSheet]);

  useEffect(() => {
    updateEdges();
    window.addEventListener('resize', updateEdges);
    return () => window.removeEventListener('resize', updateEdges);
  }, [items, open, updateEdges]);

  const handleMove = useCallback((itemKey, delta, control) => {
    const list = itemsRef.current;
    const from = list.findIndex((i) => i.itemKey === itemKey);
    if (from === -1) return;
    const item = list[from];
    const to = Math.min(Math.max(from + delta, 0), list.length - 1);
    if (to === from) {
      announce(delta < 0 ? copy.alreadyFirst(item.title) : copy.alreadyLast(item.title));
      return;
    }
    pendingFocusRef.current = { key: itemKey, control };
    movePlaylistItem(from, to);
    announce(copy.moved(item.title, to + 1, list.length));
  }, [announce, movePlaylistItem]);

  const handleRemove = useCallback((itemKey) => {
    const list = itemsRef.current;
    const index = list.findIndex((i) => i.itemKey === itemKey);
    if (index === -1) return;
    const item = list[index];
    const neighbour = list[index + 1] ?? list[index - 1];
    pendingFocusRef.current = { key: neighbour?.itemKey ?? null };
    haptics.light();
    removeFromPlaylist(itemKey);
    setUndo({ item, index });
    announce(copy.removed(item.title, list.length - 1));
  }, [announce, removeFromPlaylist]);

  const handleUndo = useCallback(() => {
    if (!undo) return;
    pendingFocusRef.current = { key: undo.item.itemKey };
    restorePlaylistItem(undo.item, undo.index);
    announce(copy.restored(undo.item.title, Math.min(undo.index, itemsRef.current.length) + 1));
    setUndo(null);
  }, [undo, announce, restorePlaylistItem]);

  const handleTileKeyDown = useCallback((event) => {
    if (locked || event.target !== event.currentTarget) return;
    const { itemKey } = event.currentTarget.dataset;
    const deltas = {
      ArrowLeft: -1,
      ArrowUp: -1,
      ArrowRight: 1,
      ArrowDown: 1,
      Home: -Infinity,
      End: Infinity,
    };
    if (event.key in deltas) {
      event.preventDefault();
      handleMove(itemKey, deltas[event.key]);
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      const item = itemsRef.current.find((i) => i.itemKey === itemKey);
      setConfirm({ kind: 'remove', item, rect: event.currentTarget.getBoundingClientRect() });
    }
  }, [locked, handleMove]);

  const closeConfirm = useCallback(() => {
    setConfirm((current) => {
      if (current?.kind === 'remove') {
        pendingFocusRef.current = { key: current.item.itemKey };
        requestAnimationFrame(() => {
          listRef.current
            ?.querySelector(`[data-testid="playlist-item"][data-item-key="${current.item.itemKey}"]`)
            ?.focus({ preventScroll: true });
        });
      }
      return null;
    });
  }, []);

  const confirmAction = useCallback(() => {
    if (confirm?.kind === 'remove') handleRemove(confirm.item.itemKey);
    if (confirm?.kind === 'clear') {
      clearPlaylist();
      setUndo(null);
    }
    if (confirm?.kind === 'new') {
      newPlaylist();
      setUndo(null);
    }
    setConfirm(null);
  }, [confirm, handleRemove, clearPlaylist, newPlaylist]);

  // "Start a new playlist" after a delivery (G-7): SV-8b when the draft has unsaved changes.
  const { dirty } = state.playlist;
  const canSave = state.savedPlaylistsStatus !== 'not_configured' && items.length > 0;
  const openNew = (event) => {
    if (!dirty || items.length === 0) {
      newPlaylist();
      setUndo(null);
      return;
    }
    setConfirm({ kind: 'new', rect: event.currentTarget.getBoundingClientRect() });
  };
  const saveThenNew = useCallback(async () => {
    setConfirm(null);
    const result = await savePlaylist();
    if (result.ok) {
      newPlaylist();
      setUndo(null);
    } else if (result.reason === 'error') {
      toast.error(saveFailed(result.error.message));
    } else if (result.reason === 'conflict') {
      toast.warning(SAVED_COPY.conflict);
    } else if (result.reason === 'playlist_not_found') {
      toast.warning(SAVED_COPY.goneTitle);
    }
  }, [savePlaylist, newPlaylist, toast]);

  const handleReorder = useCallback((next) => {
    setPlaylistOrder(next.map((i) => i.itemKey));
  }, [setPlaylistOrder]);

  const handleDragStart = useCallback((item) => {
    announce(copy.pickedUp(item.title));
  }, [announce]);

  const handleDragEnd = useCallback((item) => {
    const list = itemsRef.current;
    const index = list.findIndex((i) => i.itemKey === item.itemKey);
    haptics.light();
    announce(copy.dropped(item.title, index + 1, list.length));
  }, [announce]);

  const handleDrag = useCallback((event) => {
    const el = scrollerRef.current;
    if (!el || !Number.isFinite(event?.clientX)) return;
    const rect = el.getBoundingClientRect();
    const [pos, start, end, axis] = isSheet
      ? [event.clientY, rect.top, rect.bottom, 'scrollTop']
      : [event.clientX, rect.left, rect.right, 'scrollLeft'];
    if (pos < start + EDGE_SCROLL_ZONE) el[axis] -= EDGE_SCROLL_STEP;
    else if (pos > end - EDGE_SCROLL_ZONE) el[axis] += EDGE_SCROLL_STEP;
  }, [isSheet]);

  const openClear = (event) => setConfirm({ kind: 'clear', rect: event.currentTarget.getBoundingClientRect() });

  const capReached = items.length >= PLAYLIST_MAX_ITEMS;
  const isEmpty = items.length === 0;
  const deliveryPanel = deliveryPanelProps && <DeliveryPanel {...deliveryPanelProps} onNew={openNew} />;

  // §5.5: checks cascade left to right within 0.6 s.
  const cascadeStep = Math.min(0.05, 0.6 / Math.max(items.length, 1));
  const progressFor = (designId) => {
    const entry = state.dispatchProgress[designId]?.[delivery.agentId];
    return entry ? { [delivery.agentId]: entry } : undefined;
  };

  const tiles = items.map((item, index) => (
    <PlaylistItemTile
      key={item.itemKey}
      item={item}
      position={index + 1}
      total={items.length}
      layout={isSheet ? 'row' : 'tile'}
      status={tileStatusFor(delivery, item.itemKey)}
      progressByAgent={progressFor(item.designId)}
      agents={state.linkedAgents}
      checkDelay={delivered ? index * cascadeStep : 0}
      onRemoveGone={handleRemove}
      locked={locked}
      isArriving={arrivingKey === item.itemKey}
      onMove={handleMove}
      onRemove={handleRemove}
      onKeyDown={handleTileKeyDown}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDrag={handleDrag}
      describedBy={HELP_ID}
    />
  ));

  const clearButton = !isEmpty && !locked && (
    <button
      type="button"
      onClick={openClear}
      data-testid="playlist-clear-button"
      className="inline-flex items-center gap-1.5 flex-shrink-0 px-2.5 py-1.5 squircle-sm text-body-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-elevated-dark-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 touch-target"
    >
      <Trash2 className="w-4 h-4" />
      {COPY.clear}
    </button>
  );

  const popover = (
    <TileConfirmPopover
      isOpen={Boolean(confirm)}
      anchorRect={confirm?.rect}
      message={confirm?.kind === 'clear'
        ? clearConfirm(items.length)
        : confirm?.kind === 'new'
          ? SAVED_COPY.newTitle
          : confirm ? removeConfirm(confirm.item.title) : ''}
      confirmLabel={{ clear: COPY.clear, new: SAVED_COPY.startNew }[confirm?.kind] ?? COPY.remove}
      confirmVariant={confirm?.kind === 'new' ? 'primary' : 'danger'}
      secondaryAction={confirm?.kind === 'new' && canSave
        ? { label: SAVED_COPY.saveFirst, onClick: saveThenNew, testId: 'playlist-new-save-first' }
        : null}
      testId={{ clear: 'playlist-clear-confirm', new: 'playlist-new-confirm' }[confirm?.kind] ?? 'playlist-item-remove-confirm'}
      onConfirm={confirmAction}
      onCancel={closeConfirm}
    />
  );

  const srText = (
    <>
      <h2 id={HEADING_ID} className="sr-only">{carouselHeading(items.length)}</h2>
      <p id={HELP_ID} className="sr-only">{COPY.reorderHelp}</p>
    </>
  );

  if (isSheet) {
    return (
      <section aria-labelledby={HEADING_ID} data-testid="playlist-carousel" id="playlist-carousel" data-layout="sheet">
        {srText}
        <AnimatePresence initial={false}>
          {!open && (
            <motion.div
              key="peek"
              {...pageTransitions.fade}
              className="fixed left-0 right-0 z-20 h-16 glass border-t glass-border shadow-soft-lg"
              style={{ bottom: DOCK_BOTTOM }}
            >
              <div className="h-full flex items-center gap-3 px-4">
                <button
                  type="button"
                  onClick={() => onOpenChange(true)}
                  aria-expanded={false}
                  aria-label={sheetHandleLabel(items.length, false)}
                  data-testid="playlist-sheet-handle"
                  className="flex-1 min-w-0 h-full flex items-center gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 rounded-lg"
                >
                  {isEmpty ? (
                    <span className="text-body-sm text-gray-600 dark:text-gray-300">{COPY.emptyPeek}</span>
                  ) : (
                    <motion.span
                      key={items.length}
                      className="flex items-center gap-3 min-w-0"
                      animate={reduceMotion ? undefined : { scale: [1, 1.03, 1] }}
                      transition={{ duration: 0.3 }}
                    >
                      <MiniThumbs items={items} max={3} flightTarget="peek" small />
                      <CountLine items={items} />
                    </motion.span>
                  )}
                  <ChevronUp className="w-4 h-4 ml-auto flex-shrink-0 text-gray-500 dark:text-gray-400" />
                </button>
                {!isEmpty && deliverControl}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {open && (
            <motion.div
              key="sheet"
              role="dialog"
              aria-modal="false"
              aria-labelledby={HEADING_ID}
              {...(reduceMotion ? pageTransitions.fade : pageTransitions.slideFromBottom)}
              drag="y"
              dragListener={false}
              dragControls={sheetDrag}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              onDragEnd={(event, { offset, velocity }) => {
                if (offset.y > SHEET_CLOSE_OFFSET || velocity.y > SHEET_CLOSE_VELOCITY) onOpenChange(false);
              }}
              className="fixed left-0 right-0 z-20 flex flex-col glass border-t glass-border shadow-soft-lg rounded-t-[20px]"
              style={{ top: '12vh', bottom: DOCK_BOTTOM }}
            >
              {delivered && <GlowSweep runKey={delivery.deliveredAt} />}
              <div onPointerDown={(event) => sheetDrag.start(event)} style={{ touchAction: 'none' }}>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  aria-expanded
                  aria-label={sheetHandleLabel(items.length, true)}
                  data-testid="playlist-sheet-handle"
                  className="w-full h-11 flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 rounded-t-[20px]"
                >
                  <span className="w-9 h-1 rounded-full bg-gray-300 dark:bg-white/20" />
                </button>
                <div className="px-4 pb-3 flex items-center gap-3">
                  <ListMusic className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="text-body font-semibold text-gray-900 dark:text-gray-100 truncate">{state.playlist.name}</p>
                    <CountLine items={items} />
                  </div>
                  {clearButton}
                </div>
                {(capReached || undo || deliveryPanel || delivery.edited) && (
                  <div className="px-4 pb-2 space-y-2">
                    {capReached && <CapNotice />}
                    {deliveryPanel}
                    <DeliveryStatusStrip undo={undo} onUndo={handleUndo} edited={delivery.edited} panelShown={Boolean(deliveryPanel)} />
                  </div>
                )}
              </div>
              <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain border-t border-gray-200 dark:border-white/5">
                <div ref={listRef}>
                  {isEmpty ? (
                    <EmptyGuidance isRow canvaConnected={canvaConnected} onAdd={onFocusSearch} />
                  ) : (
                    <Reorder.Group
                      as="ol"
                      role="list"
                      axis="y"
                      values={items}
                      onReorder={handleReorder}
                      className="flex flex-col divide-y divide-gray-200 dark:divide-elevated-dark-3"
                    >
                      {tiles}
                    </Reorder.Group>
                  )}
                </div>
              </div>
              <div className="p-4 space-y-2 border-t border-gray-200 dark:border-white/5">
                <button
                  type="button"
                  onClick={onFocusSearch}
                  data-add-tile
                  className="w-full touch-target inline-flex items-center justify-center gap-2 squircle-sm border-2 border-dashed border-primary-400/70 text-primary-700 dark:text-primary-300 text-body-sm font-semibold hover:bg-primary-500/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <Plus className="w-4 h-4" strokeWidth={2.5} />
                  {COPY.pickerHeading}
                </button>
                {!isEmpty && deliverControl}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {popover}
      </section>
    );
  }

  const maskStops = `${edges.start ? 'transparent 0, black 24px' : 'black 0'}, ${edges.end ? 'black calc(100% - 24px), transparent 100%' : 'black 100%'}`;
  const edgeMask = edges.start || edges.end
    ? { maskImage: `linear-gradient(to right, ${maskStops})`, WebkitMaskImage: `linear-gradient(to right, ${maskStops})` }
    : {};

  return (
    <section
      aria-labelledby={HEADING_ID}
      data-testid="playlist-carousel"
      id="playlist-carousel"
      data-layout="strip"
      className="fixed left-0 right-0 z-20 glass border-t glass-border shadow-soft-lg"
      style={{ bottom: DOCK_BOTTOM }}
    >
      {srText}
      {delivered && <GlowSweep runKey={delivery.deliveredAt} />}
      <div className="max-w-7xl mx-auto pl-4 sm:pl-6 lg:pl-8 pr-[72px]">
        <div className="h-12 flex items-center gap-3">
          <ListMusic className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" aria-hidden="true" />
          {!open && !isEmpty && <MiniThumbs items={items} max={5} flightTarget="peek" />}
          {isEmpty && !open ? (
            <p className="text-body-sm text-gray-600 dark:text-gray-300 truncate">{COPY.emptyTitle}</p>
          ) : (
            <CountLine items={items} />
          )}
          <div className="flex-1 min-w-0 flex items-center gap-3">
            {capReached && <CapNotice />}
            <DeliveryStatusStrip undo={undo} onUndo={handleUndo} edited={delivery.edited} panelShown={Boolean(deliveryPanel)} />
          </div>
          {open && clearButton}
          <button
            type="button"
            disabled={locked}
            onClick={() => onOpenChange(!open)}
            aria-expanded={open}
            aria-controls="playlist-carousel-body"
            aria-label={open ? COPY.collapse : COPY.expand}
            data-testid="playlist-collapse-toggle"
            className="flex-shrink-0 touch-target inline-flex items-center justify-center squircle-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-elevated-dark-2 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            {open ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </button>
        </div>
        <motion.div
          id="playlist-carousel-body"
          initial={false}
          animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }}
          transition={reduceMotion ? { duration: 0.15 } : springs.gentle}
          className="overflow-hidden"
          aria-hidden={!open}
          inert={open ? undefined : ''}
        >
          {deliveryPanel && <div className="pb-3">{deliveryPanel}</div>}
          <motion.div
            ref={scrollerRef}
            layoutScroll
            onScroll={updateEdges}
            className="overflow-x-auto overflow-y-hidden pb-3 pt-0.5 -mx-2 px-2 [transform:translateZ(0)]"
            style={{ scrollSnapType: 'x proximity', ...edgeMask }}
          >
            <div ref={listRef} className="flex items-start gap-3">
              {isEmpty ? (
                <EmptyGuidance canvaConnected={canvaConnected} onAdd={onFocusSearch} />
              ) : (
                <>
                  <Reorder.Group
                    as="ol"
                    role="list"
                    axis="x"
                    values={items}
                    onReorder={handleReorder}
                    className="flex items-start gap-3"
                  >
                    {tiles}
                  </Reorder.Group>
                  {!capReached && !locked && (
                    <button
                      type="button"
                      onClick={onFocusSearch}
                      data-add-tile
                      className="flex-shrink-0 w-[128px] xl:w-[152px] aspect-video mt-1.5 squircle-sm border-2 border-dashed border-gray-300 dark:border-white/15 flex flex-col items-center justify-center gap-1 text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-600 dark:hover:text-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
                    >
                      <Plus className="w-5 h-5" strokeWidth={2.5} />
                      <span className="text-caption">{COPY.addTile}</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
      {popover}
    </section>
  );
}
